/**
 * TypeScript types for Demand Review workflow.
 *
 * Supports reviewing externally drafted demands,
 * parsing Bates citations, and AI-powered verification.
 */

// ---------------------------------------------------------------------------
// Enums
// ---------------------------------------------------------------------------

export enum DemandReviewWorkspaceStatus {
  CREATED = 'created',
  UPLOADING_DEMAND = 'uploading_demand',
  TRANSFORMING = 'transforming',
  PARSING_CITATIONS = 'parsing_citations',
  READY_FOR_EXHIBITS = 'ready_for_exhibits',
  MATCHING_CITATIONS = 'matching_citations',
  READY_FOR_VERIFICATION = 'ready_for_verification',
  VERIFYING = 'verifying',
  COMPLETED = 'completed',
  FAILED = 'failed',
}

export enum CitationVerificationStatus {
  PENDING = 'pending',
  IN_PROGRESS = 'in_progress',
  VERIFIED_SUPPORTED = 'verified_supported',
  VERIFIED_NOT_SUPPORTED = 'verified_not_supported',
  VERIFIED_PARTIAL = 'verified_partial',
  SKIPPED = 'skipped',
  FAILED = 'failed',
}

export enum CitationVerificationResult {
  SUPPORTED = 'supported',
  NOT_SUPPORTED = 'not_supported',
  PARTIAL = 'partial',
  UNABLE_TO_ASSESS = 'unable_to_assess',
}

// ---------------------------------------------------------------------------
// Base Types
// ---------------------------------------------------------------------------

export interface BaseDemandReviewWorkspace {
  name: string;
  bates_pattern?: string | null;
}

export interface BaseDemandReviewExhibit {
  name: string;
  blob_id: string;
}

export interface BaseDemandReviewCitation {
  bates_number: string;
  context_text: string;
}

// ---------------------------------------------------------------------------
// Full Types
// ---------------------------------------------------------------------------

export interface DemandReviewWorkspace extends BaseDemandReviewWorkspace {
  id: string;
  matter_id: string;
  status: DemandReviewWorkspaceStatus;
  demand_blob_id: string | null;
  demand_pdf_blob_id: string | null;
  demand_artifact_id: string | null; // Artifact ID for the demand PDF (for citations)
  demand_markdown: string | null;
  error_message: string | null;
  created_at: string;
  modified_at: string;
  exhibit_count: number;
  citation_count: number;
  verified_count: number;
  pending_verification_count: number;
}

export interface DemandReviewExhibit extends BaseDemandReviewExhibit {
  id: string;
  workspace_id: string;
  order: number;
  page_count: number;
  created_at: string;
  modified_at: string;
}

export interface CitationVerificationResultDetails {
  result: CitationVerificationResult;
  explanation: string;
  confidence: number;
  supporting_text?: string | null;
}

export interface DemandReviewCitation extends BaseDemandReviewCitation {
  id: string;
  workspace_id: string;
  bates_prefix: string;
  page_start: number | null;
  page_end: number | null;
  position_start: number | null;
  position_end: number | null;
  exhibit_id: string | null;
  exhibit_page_number: number | null;
  verification_status: CitationVerificationStatus;
  verification_result: CitationVerificationResultDetails | null;
  created_at: string;
  modified_at: string;
}

// ---------------------------------------------------------------------------
// Create/Update Types
// ---------------------------------------------------------------------------

export interface CreateDemandReviewWorkspace {
  name: string;
  bates_pattern?: string | null;
}

export interface UpdateDemandReviewWorkspace {
  name?: string;
  bates_pattern?: string | null;
}

export interface CreateDemandReviewExhibit {
  name: string;
  blob_id: string;
  index?: number | null;
}

export interface UpdateDemandReviewExhibit {
  name?: string;
  index?: number | null;
}

// ---------------------------------------------------------------------------
// API Response Types
// ---------------------------------------------------------------------------

export interface MatchCitationsResponse {
  matched_count: number;
}

export interface VerifyAllCitationsResponse {
  verification_count: number;
}

// ---------------------------------------------------------------------------
// Filter/Query Types
// ---------------------------------------------------------------------------

export interface DemandReviewCitationFilters {
  verification_status?: CitationVerificationStatus;
}

// ---------------------------------------------------------------------------
// Proofreading Types
// ---------------------------------------------------------------------------

export enum ProofreadingJobStatus {
  PENDING = 'pending',
  PROCESSING = 'processing',
  COMPLETED = 'completed',
  FAILED = 'failed',
  CANCELLED = 'cancelled',
}

// Workflow status response from polling the Temporal workflow
export interface WorkflowStatusResponse {
  status: string;
  status_description: string;
  error_message: string | null;
  citation_count: number;
  proofreading: {
    is_running: boolean;
    total_rules: number;
    processed_rules: number;
  };
}

export enum ProofreadingRuleResultStatus {
  PROCESSING = 'processing',
  NO_ISSUES_FOUND = 'no_issues_found',
  MINOR_ISSUES_FOUND = 'minor_issues_found',
  MODERATE_ISSUES_FOUND = 'moderate_issues_found',
  CRITICAL_ISSUES_FOUND = 'critical_issues_found',
  UNABLE_TO_ASSESS = 'unable_to_assess',
  FAILED = 'failed',
}

export enum ProofreadingFindingType {
  ISSUE = 'issue',
  EVIDENCE = 'evidence',
}

export interface ProofreadingFinding {
  id: string;
  finding_type: ProofreadingFindingType;
  description: string;
  citation_ids: string[]; // All citations (demand and case files)
  section_name?: string | null;
  human_response?: string | null;
  responded_by?: string | null;
  responded_at?: string | null;
}

export interface ProofreadingRuleResult {
  id: string;
  rule_id: string;
  title: string;
  status: ProofreadingRuleResultStatus;
  reason?: string | null;
  findings: ProofreadingFinding[];
}

export interface ProofreadingJob {
  id: string;
  status: ProofreadingJobStatus;
  workspace_id: string;
  version?: number | null;
  created_at: string;
  completed_at?: string | null;
  results: ProofreadingRuleResult[];
}

// ---------------------------------------------------------------------------
// Utility Types
// ---------------------------------------------------------------------------

/**
 * Check if a citation has been verified (any verification status except pending/in_progress)
 */
export function isCitationVerified(
  status: CitationVerificationStatus,
): boolean {
  return [
    CitationVerificationStatus.VERIFIED_SUPPORTED,
    CitationVerificationStatus.VERIFIED_NOT_SUPPORTED,
    CitationVerificationStatus.VERIFIED_PARTIAL,
  ].includes(status);
}

/**
 * Get a display label for verification status
 */
export function getVerificationStatusLabel(
  status: CitationVerificationStatus,
): string {
  const labels: Record<CitationVerificationStatus, string> = {
    [CitationVerificationStatus.PENDING]: 'Pending',
    [CitationVerificationStatus.IN_PROGRESS]: 'Verifying...',
    [CitationVerificationStatus.VERIFIED_SUPPORTED]: 'Supported',
    [CitationVerificationStatus.VERIFIED_NOT_SUPPORTED]: 'Not Supported',
    [CitationVerificationStatus.VERIFIED_PARTIAL]: 'Partially Supported',
    [CitationVerificationStatus.SKIPPED]: 'Skipped',
    [CitationVerificationStatus.FAILED]: 'Failed',
  };
  return labels[status] || status;
}

/**
 * Get a color class for verification status (for badges/indicators)
 */
export function getVerificationStatusColor(
  status: CitationVerificationStatus,
): 'green' | 'red' | 'yellow' | 'gray' | 'blue' {
  switch (status) {
    case CitationVerificationStatus.VERIFIED_SUPPORTED:
      return 'green';
    case CitationVerificationStatus.VERIFIED_NOT_SUPPORTED:
      return 'red';
    case CitationVerificationStatus.VERIFIED_PARTIAL:
      return 'yellow';
    case CitationVerificationStatus.IN_PROGRESS:
      return 'blue';
    case CitationVerificationStatus.PENDING:
    case CitationVerificationStatus.SKIPPED:
    case CitationVerificationStatus.FAILED:
    default:
      return 'gray';
  }
}
